import { graphql, useStaticQuery } from "gatsby";
import React, { useMemo } from "react";
import ReactDOM from "react-dom";
import ReactTooltip from "react-tooltip";
import { formatPrice } from "../../utility/prices";
import Icon from "../atoms/icon";
import Parser from "html-react-parser";
import ProductNotes from "../atoms/product-notes";
import classNames from "classnames";

const parseRaee = (contributoRaee) =>
  parseFloat(contributoRaee?.replace(/[^0-9,.]/g, "")?.replace(/[^0-9]/g, "."))?.toFixed(2);

const PriceFormatted = ({ priceLanguage, priceValue, currency, save, origin }) => {
  const options = currency ? { style: "currency", currency } : { style: "percent" };
  const formatted =
    currency && priceLanguage === "tr-TR"
      ? formatPrice(priceValue, currency, priceLanguage)
      : Intl.NumberFormat(priceLanguage, options).format(priceValue);
  return (!save && origin === "tag" ? "-" : "") + formatted.toString();
};

export const PriceAbs = ({ priceLanguage, priceData, currency, omnibusPrice, save, origin }) => {
  const priceValue = omnibusPrice
    ? omnibusPrice - priceData.sellingPrice?.value
    : priceData.msrpPrice?.value - priceData.sellingPrice?.value;
  return (
    <PriceFormatted
      priceLanguage={priceLanguage}
      priceValue={priceValue}
      currency={currency}
      save={save}
      origin={origin}
    />
  );
};

export const PricePerc = ({
  priceLanguage,
  priceData,
  contributoRaee,
  priceWithWeee,
  omnibusPrice,
  save,
  origin,
}) => {
  const contributoRaeeFormatted = contributoRaee ? parseRaee(contributoRaee) : null;
  const priceValue = priceData?.msrpPrice?.value
    ? priceWithWeee === true && contributoRaee
      ? (priceData.msrpPrice.value - priceData.sellingPrice.value) /
        (priceData.msrpPrice.value - contributoRaeeFormatted)
      : (priceData.msrpPrice.value - priceData.sellingPrice.value) / priceData.msrpPrice.value
    : null;
  const priceValueOmnibus = omnibusPrice
    ? priceWithWeee === true && contributoRaee
      ? (omnibusPrice - priceData.sellingPrice.value) / (omnibusPrice - contributoRaeeFormatted)
      : (omnibusPrice - priceData.sellingPrice.value) / omnibusPrice
    : null;
  return (
    <PriceFormatted
      priceLanguage={priceLanguage}
      priceValue={priceValueOmnibus ? priceValueOmnibus : priceValue}
      save={save}
      origin={origin}
    />
  );
};

const ProductPrice = ({ data, origin, contributoRaee, noMsrp, part, index }) => {
  const {
    config,
    labelManagerLabels,
    site: { siteMetadata },
    productNote,
  } = useStaticQuery(graphql`
    {
      config: liferayGeneralConfiguration {
        currency
        priceType
        priceWithWeee
      }
      site {
        siteMetadata {
          priceLanguage
        }
      }
      labelManagerLabels: labelManagerLabels {
        pricePercentageTooltip
        suggestedPriceInfo
        suggestedPrice
        omnibusLabel
        msrpLabel
      }
      productNote: liferayProductNotes {
        ...ProductNotesFragment
      }
    }
  `);

  const currency = config.currency || "EUR";
  const priceData = data;

  const sellingPrice = priceData?.sellingPrice?.value;
  const msrpPrice = priceData?.msrpPrice?.value;

  const contributoRaeeMulti = useMemo(
    () =>
      priceData?.contributoRaee?.[0]?.value
        ? formatPrice(
            JSON.parse(priceData.contributoRaee?.[0].value)?.amount,
            priceData.sellingPrice?.currency || currency,
            siteMetadata.priceLanguage
          )
        : null,
    [priceData, siteMetadata.priceLanguage, currency]
  );

  const omnibusPrice = useMemo(
    () =>
      priceData?.omnibusPrice?.[0]?.value
        ? formatPrice(
            JSON.parse(priceData.omnibusPrice?.[0]?.value)?.amount,
            JSON.parse(priceData.omnibusPrice?.[0].value)?.currency_code || currency,
            siteMetadata.priceLanguage
          )
        : null,
    [priceData, siteMetadata.priceLanguage, currency]
  );

  const omnibusNotFormatted = priceData?.omnibusPrice?.[0]?.value
    ? JSON?.parse(priceData?.omnibusPrice?.[0]?.value)?.amount
    : null;

  const msrp = msrpPrice
    ? `${labelManagerLabels?.msrpLabel}:
    ${formatPrice(msrpPrice, currency, siteMetadata.priceLanguage)}`
    : null;

  return (
    <>
      {origin === "compare-overlay" ? (
        <div className="compare-overlay--open--product--price">
          {formatPrice(sellingPrice, currency, siteMetadata.priceLanguage)}
          {((omnibusPrice && !noMsrp && omnibusNotFormatted > sellingPrice) ||
            (!omnibusPrice && msrpPrice && msrpPrice > sellingPrice)) && (
            <div className="compare-overlay--open--product--discount">
              {omnibusPrice ? (
                <span className="old-price">{omnibusPrice}</span>
              ) : msrpPrice ? (
                <span className="old-price">
                  {formatPrice(msrpPrice, currency, siteMetadata.priceLanguage)}
                </span>
              ) : null}
              {config.priceType === "perc and absolute" ? (
                <span className="discount">
                  <PriceAbs
                    priceLanguage={siteMetadata.priceLanguage}
                    priceData={priceData}
                    currency={config.currency}
                    omnibusPrice={
                      omnibusNotFormatted && omnibusNotFormatted > sellingPrice
                        ? omnibusNotFormatted
                        : null
                    }
                  />
                  (
                  <PricePerc
                    priceLanguage={siteMetadata.priceLanguage}
                    priceData={priceData}
                    contributoRaee={contributoRaeeMulti}
                    priceWithWeee={config.priceWithWeee}
                    omnibusPrice={
                      omnibusNotFormatted && omnibusNotFormatted > sellingPrice
                        ? omnibusNotFormatted
                        : null
                    }
                  />
                  )
                  {labelManagerLabels?.pricePercentageTooltip && (
                    <>
                      <p
                        className="product-detail__priceInfo"
                        data-for={
                          !!index && index !== undefined
                            ? `productPriceInfo-${index}-overlay`
                            : "productPriceInfo-overlay"
                        }
                        data-tip
                      >
                        <Icon name="symbols-info-price" />
                      </p>
                      {ReactDOM.createPortal(
                        <ReactTooltip
                          id={
                            !!index && index !== undefined
                              ? `productPriceInfo-${index}-overlay`
                              : "productPriceInfo-overlay"
                          }
                          place="top"
                          effect="solid"
                          textColor="#0a2240"
                          type="light"
                          className="productPriceInfo"
                        >
                          <Icon name="symbols-info-price" />
                          {Parser(labelManagerLabels?.pricePercentageTooltip)}
                        </ReactTooltip>,
                        document.body
                      )}
                    </>
                  )}
                </span>
              ) : config.priceType === "only perc" ? (
                <span className="discount">
                  <PricePerc
                    priceLanguage={siteMetadata.priceLanguage}
                    priceData={priceData}
                    contributoRaee={contributoRaeeMulti}
                    priceWithWeee={config.priceWithWeee}
                    omnibusPrice={
                      omnibusNotFormatted && omnibusNotFormatted > sellingPrice
                        ? omnibusNotFormatted
                        : null
                    }
                  />
                  {labelManagerLabels?.pricePercentageTooltip && (
                    <>
                      <p
                        className="product-detail__priceInfo"
                        data-for={
                          !!index && index !== undefined
                            ? `productPriceInfo-${index}-overlay`
                            : "productPriceInfo-overlay"
                        }
                        data-tip
                      >
                        <Icon name="symbols-info-price" />
                      </p>
                      {ReactDOM.createPortal(
                        <ReactTooltip
                          id={
                            !!index && index !== undefined
                              ? `productPriceInfo-${index}-overlay`
                              : "productPriceInfo-overlay"
                          }
                          place="top"
                          effect="solid"
                          textColor="#0a2240"
                          type="light"
                          className="productPriceInfo"
                        >
                          <Icon name="symbols-info-price" />
                          {Parser(labelManagerLabels?.pricePercentageTooltip)}
                        </ReactTooltip>,
                        document.body
                      )}
                    </>
                  )}
                </span>
              ) : config.priceType === "only abs" ? (
                <span className="discount_calc">
                  <PriceAbs
                    priceLanguage={siteMetadata.priceLanguage}
                    priceData={priceData}
                    currency={config.currency}
                    omnibusPrice={
                      omnibusNotFormatted && omnibusNotFormatted > sellingPrice
                        ? omnibusNotFormatted
                        : null
                    }
                  />
                </span>
              ) : null}
            </div>
          )}
        </div>
      ) : origin === "compare-card-container" ? (
        <div className="product-card__price">
          <div className="pageCompare__banner-card-price">
            <span className="value-price">
              {formatPrice(sellingPrice, currency, siteMetadata.priceLanguage)}
            </span>
            {((omnibusPrice && !noMsrp && omnibusNotFormatted > sellingPrice) ||
              (!omnibusPrice && msrpPrice && msrpPrice > sellingPrice)) && (
              <div className="pageCompare__banner-card-discount">
                {omnibusPrice ? (
                  <span className="old-price">{omnibusPrice}</span>
                ) : msrpPrice ? (
                  <span className="old-price">
                    {formatPrice(msrpPrice, currency, siteMetadata.priceLanguage)}
                  </span>
                ) : null}
                {config.priceType === "perc and absolute" ? (
                  <span className="discount">
                    <PriceAbs
                      priceLanguage={siteMetadata.priceLanguage}
                      priceData={priceData}
                      currency={config.currency}
                      omnibusPrice={
                        omnibusNotFormatted && omnibusNotFormatted > sellingPrice
                          ? omnibusNotFormatted
                          : null
                      }
                    />
                    (
                    <PricePerc
                      priceLanguage={siteMetadata.priceLanguage}
                      priceData={priceData}
                      contributoRaee={contributoRaeeMulti}
                      priceWithWeee={config.priceWithWeee}
                      omnibusPrice={
                        omnibusNotFormatted && omnibusNotFormatted > sellingPrice
                          ? omnibusNotFormatted
                          : null
                      }
                    />
                    )
                    {labelManagerLabels?.pricePercentageTooltip && (
                      <>
                        <p
                          className="product-detail__priceInfo"
                          data-for={
                            !!index && index !== undefined
                              ? `productPriceInfo-${index}-compare-card`
                              : "productPriceInfo-compare-card"
                          }
                          data-tip
                        >
                          <Icon name="symbols-info-price" />
                        </p>
                        {ReactDOM.createPortal(
                          <ReactTooltip
                            id={
                              !!index && index !== undefined
                                ? `productPriceInfo-${index}-compare-card`
                                : "productPriceInfo-compare-card"
                            }
                            place="top"
                            effect="solid"
                            textColor="#0a2240"
                            type="light"
                            className="productPriceInfo"
                          >
                            <Icon name="symbols-info-price" />
                            {Parser(labelManagerLabels?.pricePercentageTooltip)}
                          </ReactTooltip>,
                          document.body
                        )}
                      </>
                    )}
                  </span>
                ) : config.priceType === "only perc" ? (
                  <span className="discount">
                    <PricePerc
                      priceLanguage={siteMetadata.priceLanguage}
                      priceData={priceData}
                      contributoRaee={contributoRaeeMulti}
                      priceWithWeee={config.priceWithWeee}
                      omnibusPrice={
                        omnibusNotFormatted && omnibusNotFormatted > sellingPrice
                          ? omnibusNotFormatted
                          : null
                      }
                    />
                    {labelManagerLabels?.pricePercentageTooltip && (
                      <>
                        <p
                          className="product-detail__priceInfo"
                          data-for={
                            !!index && index !== undefined
                              ? `productPriceInfo-${index}-compare-card`
                              : "productPriceInfo-compare-card"
                          }
                          data-tip
                        >
                          <Icon name="symbols-info-price" />
                        </p>
                        {ReactDOM.createPortal(
                          <ReactTooltip
                            id={
                              !!index && index !== undefined
                                ? `productPriceInfo-${index}-compare-card`
                                : "productPriceInfo-compare-card"
                            }
                            place="top"
                            effect="solid"
                            textColor="#0a2240"
                            type="light"
                            className="productPriceInfo"
                          >
                            <Icon name="symbols-info-price" />
                            {Parser(labelManagerLabels?.pricePercentageTooltip)}
                          </ReactTooltip>,
                          document.body
                        )}
                      </>
                    )}
                  </span>
                ) : config.priceType === "only abs" ? (
                  <span className="discount_calc">
                    <PriceAbs
                      priceLanguage={siteMetadata.priceLanguage}
                      priceData={priceData}
                      currency={config.currency}
                      omnibusPrice={
                        omnibusNotFormatted && omnibusNotFormatted > sellingPrice
                          ? omnibusNotFormatted
                          : null
                      }
                    />
                  </span>
                ) : null}
              </div>
            )}
          </div>
        </div>
      ) : origin === "sticky" ? (
        <div className="sticky-bar__product-price">
          <span className="basic-price">
            {formatPrice(sellingPrice, currency, siteMetadata.priceLanguage)}{" "}
          </span>
          {((omnibusPrice && omnibusNotFormatted > sellingPrice) ||
            (!omnibusPrice && msrpPrice && msrpPrice > sellingPrice)) && (
            <span className="sticky-bar__discount-container">
              {omnibusPrice ? (
                <span className="old-price">{omnibusPrice}</span>
              ) : msrpPrice ? (
                <span className="old-price">
                  {formatPrice(msrpPrice, currency, siteMetadata.priceLanguage)}{" "}
                </span>
              ) : null}
              {config.priceType === "perc and absolute" ? (
                <span className="discount-price">
                  <PriceAbs
                    priceLanguage={siteMetadata.priceLanguage}
                    priceData={priceData}
                    currency={config.currency}
                    omnibusPrice={
                      omnibusNotFormatted && omnibusNotFormatted > sellingPrice
                        ? omnibusNotFormatted
                        : null
                    }
                  />
                  (
                  <PricePerc
                    priceLanguage={siteMetadata.priceLanguage}
                    priceData={priceData}
                    contributoRaee={contributoRaee}
                    priceWithWeee={config.priceWithWeee}
                    omnibusPrice={
                      omnibusNotFormatted && omnibusNotFormatted > sellingPrice
                        ? omnibusNotFormatted
                        : null
                    }
                  />
                  )
                  {labelManagerLabels?.pricePercentageTooltip && (
                    <>
                      <p
                        className="product-detail__priceInfo"
                        data-for={
                          !!index && index !== undefined
                            ? `productPriceInfo-${index}-sticky`
                            : "productPriceInfo-sticky"
                        }
                        data-tip
                      >
                        <Icon name="symbols-info-price" />
                      </p>
                      {ReactDOM.createPortal(
                        <ReactTooltip
                          id={
                            !!index && index !== undefined
                              ? `productPriceInfo-${index}-sticky`
                              : "productPriceInfo-sticky"
                          }
                          place="top"
                          effect="solid"
                          textColor="#0a2240"
                          type="light"
                          className="productPriceInfo"
                        >
                          <Icon name="symbols-info-price" />
                          {Parser(labelManagerLabels?.pricePercentageTooltip)}
                        </ReactTooltip>,
                        document.body
                      )}
                    </>
                  )}
                </span>
              ) : config.priceType === "only perc" ? (
                <span className="discount-price">
                  <PricePerc
                    priceLanguage={siteMetadata.priceLanguage}
                    priceData={priceData}
                    contributoRaee={contributoRaee}
                    priceWithWeee={config.priceWithWeee}
                    omnibusPrice={
                      omnibusNotFormatted && omnibusNotFormatted > sellingPrice
                        ? omnibusNotFormatted
                        : null
                    }
                  />
                  {labelManagerLabels?.pricePercentageTooltip && (
                    <>
                      <p
                        className="product-detail__priceInfo"
                        data-for={
                          !!index && index !== undefined
                            ? `productPriceInfo-${index}-sticky`
                            : "productPriceInfo-sticky"
                        }
                        data-tip
                      >
                        <Icon name="symbols-info-price" />
                      </p>
                      {ReactDOM.createPortal(
                        <ReactTooltip
                          id={
                            !!index && index !== undefined
                              ? `productPriceInfo-${index}-sticky`
                              : "productPriceInfo-sticky"
                          }
                          place="top"
                          effect="solid"
                          textColor="#0a2240"
                          type="light"
                          className="productPriceInfo"
                        >
                          <Icon name="symbols-info-price" />
                          {Parser(labelManagerLabels?.pricePercentageTooltip)}
                        </ReactTooltip>,
                        document.body
                      )}
                    </>
                  )}
                </span>
              ) : config.priceType === "only abs" ? (
                <span className="discount_calc">
                  <PriceAbs
                    priceLanguage={siteMetadata.priceLanguage}
                    priceData={priceData}
                    currency={config.currency}
                    omnibusPrice={
                      omnibusNotFormatted && omnibusNotFormatted > sellingPrice
                        ? omnibusNotFormatted
                        : null
                    }
                  />
                </span>
              ) : null}
            </span>
          )}
        </div>
      ) : (
        <div
          className={classNames(
            "product-card__price",
            origin === "pdp" && "product-card__price--pdp"
          )}
        >
          <span className="price">
            {formatPrice(sellingPrice, currency, siteMetadata.priceLanguage)}
            {!!productNote && productNote?.textPopUp !== "" && part !== "pdi" && "*"}
          </span>
          {((omnibusPrice && !noMsrp && omnibusNotFormatted > sellingPrice) ||
            (!omnibusPrice && msrpPrice && msrpPrice > sellingPrice)) && (
            <div className="d-flex product-detail__msrp">
              {omnibusPrice ? (
                <div>
                  {`${labelManagerLabels?.omnibusLabel}: `}
                  <span
                    className={classNames(
                      "",
                      (config.priceType === "perc and absolute" ||
                        config.priceType === "only perc" ||
                        config.priceType === "only abs") &&
                        "product-detail__msrp--discount"
                    )}
                  >
                    {omnibusPrice}
                  </span>
                </div>
              ) : msrpPrice ? (
                <div>
                  <span className="product-detail__msrp--discount">
                    {formatPrice(msrpPrice, currency, siteMetadata.priceLanguage)}
                  </span>
                </div>
              ) : null}
              <span className="discount">
                {config.priceType === "perc and absolute" ? (
                  <span className="discount_calc">
                    <PriceAbs
                      priceLanguage={siteMetadata.priceLanguage}
                      priceData={priceData}
                      currency={config.currency}
                      omnibusPrice={
                        omnibusNotFormatted && omnibusNotFormatted > sellingPrice
                          ? omnibusNotFormatted
                          : null
                      }
                    />
                    (
                    <PricePerc
                      priceLanguage={siteMetadata.priceLanguage}
                      priceData={priceData}
                      contributoRaee={contributoRaee}
                      priceWithWeee={config.priceWithWeee}
                      omnibusPrice={
                        omnibusNotFormatted && omnibusNotFormatted > sellingPrice
                          ? omnibusNotFormatted
                          : null
                      }
                    />
                    )
                    {labelManagerLabels?.pricePercentageTooltip && (
                      <>
                        <p
                          className="product-detail__priceInfo"
                          data-for={
                            !!index && index !== undefined
                              ? `productPriceInfo-${index}`
                              : "productPriceInfo"
                          }
                          data-tip
                        >
                          <Icon name="symbols-info-price" />
                        </p>
                        {ReactDOM.createPortal(
                          <ReactTooltip
                            id={
                              !!index && index !== undefined
                                ? `productPriceInfo-${index}`
                                : "productPriceInfo"
                            }
                            place="top"
                            effect="solid"
                            textColor="#0a2240"
                            type="light"
                            className="productPriceInfo"
                          >
                            <Icon name="symbols-info-price" />
                            {Parser(labelManagerLabels?.pricePercentageTooltip)}
                          </ReactTooltip>,
                          document.body
                        )}
                      </>
                    )}
                  </span>
                ) : config.priceType === "only perc" ? (
                  <span className="discount_calc">
                    <PricePerc
                      priceLanguage={siteMetadata.priceLanguage}
                      priceData={priceData}
                      contributoRaee={contributoRaee}
                      priceWithWeee={config.priceWithWeee}
                      omnibusPrice={
                        omnibusNotFormatted && omnibusNotFormatted > sellingPrice
                          ? omnibusNotFormatted
                          : null
                      }
                    />
                    {labelManagerLabels?.pricePercentageTooltip && (
                      <>
                        <p
                          className="product-detail__priceInfo"
                          data-for={
                            !!index && index !== undefined
                              ? `productPriceInfo-${index}`
                              : "productPriceInfo"
                          }
                          data-tip
                        >
                          <Icon name="symbols-info-price" />
                        </p>
                        {ReactDOM.createPortal(
                          <ReactTooltip
                            id={
                              !!index && index !== undefined
                                ? `productPriceInfo-${index}`
                                : "productPriceInfo"
                            }
                            place="top"
                            effect="solid"
                            textColor="#0a2240"
                            type="light"
                            className="productPriceInfo"
                          >
                            <Icon name="symbols-info-price" />
                            {Parser(labelManagerLabels?.pricePercentageTooltip)}
                          </ReactTooltip>,
                          document.body
                        )}
                      </>
                    )}
                  </span>
                ) : config.priceType === "only abs" ? (
                  <span className="discount_calc">
                    <PriceAbs
                      priceLanguage={siteMetadata.priceLanguage}
                      priceData={priceData}
                      currency={config.currency}
                      omnibusPrice={
                        omnibusNotFormatted && omnibusNotFormatted > sellingPrice
                          ? omnibusNotFormatted
                          : null
                      }
                    />
                  </span>
                ) : null}
              </span>
            </div>
          )}
          {msrpPrice &&
            msrpPrice !== "0.0" &&
            msrpPrice > sellingPrice &&
            msrpPrice > omnibusNotFormatted &&
            omnibusPrice && (
              <span className="discount_calc">
                <span className="old_price">{msrp}</span>
                {labelManagerLabels?.suggestedPriceInfo && (
                  <>
                    <p
                      className="product-detail__priceInfo"
                      data-for={
                        !!index && index !== undefined
                          ? `suggestedPriceInfo-${index}`
                          : "suggestedPriceInfo"
                      }
                      data-tip
                    >
                      <Icon name="symbols-info-price" />
                    </p>
                    {ReactDOM.createPortal(
                      <ReactTooltip
                        id={
                          !!index && index !== undefined
                            ? `suggestedPriceInfo-${index}`
                            : "suggestedPriceInfo"
                        }
                        place="top"
                        effect="solid"
                        textColor="#0a2240"
                        type="light"
                        className="productPriceInfo"
                      >
                        <Icon name="symbols-info-price" />
                        {Parser(labelManagerLabels?.suggestedPriceInfo)}
                      </ReactTooltip>,
                      document.body
                    )}
                  </>
                )}
              </span>
            )}
        </div>
      )}
      {!!productNote && !!priceData && origin === "pdp" && (
        <ProductNotes data={productNote} contributoRaee={contributoRaee} position="pdp" />
      )}
    </>
  );
};
export default ProductPrice;
